import { Table, Button, Form, Dropdown, Col, InputGroup } from "react-bootstrap"
import { useState } from "react"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import { X, CirclePlus } from 'lucide-react'

import filterBySearch from "../../utils/filterBySearch"
import filterBySortBy from "../../utils/filterBySortBy"
import { useSendInviteMutation, useRemoveUserFromClubMutation, useFetchClubMembersQuery } from "../../app/clubsApiSlice"
import TypeToConfirmButton from "../../components/buttons/TypeToConfirmButton"
import LoadingTextSwap from "../../components/LoadingTextSwap"
import useAuth from "../../hooks/useAuth"
import TrBtn from "../../components/buttons/table/TrBtn"
import PositionBadges from "../../components/PositionBadges"
import Spinner from "../../components/Spinner"
import DisplayErrorMessage from "../../components/DisplayErrorMessage"
import AddPosition from "./components/AddPosition"
import transformClubMembers from "../../utils/transformClubMembers"
import ViewUserButton from "../../components/buttons/table/ViewUserButton"

const ViewClubMembers = ({ club, clubId }) => {
  const navigate = useNavigate()
  const { role } = useAuth()

  const { data: membersData, isSuccess, isLoading, isError, error } = useFetchClubMembersQuery({ clubId, staff: club.staffById })
  const [sendInvite, { isLoading: inviteSending }] = useSendInviteMutation()
  const [removeUserFromClub, { isLoading: deleting }] = useRemoveUserFromClubMutation()

  const [inviteError, setInviteError] = useState("")
  const [userId, setUserId] = useState("")

  const [sortedBy, setSortedBy] = useState("position")
  const [searchBy, setSearchBy] = useState("")
  const [reverseSort, setReverseSort] = useState(false)

  const onUserIdChanged = (e) => {
    setUserId(e.target.value.toUpperCase())
    setInviteError("")
  }

  const handleSortBy = (sortBy) => {
    if (sortedBy === sortBy) {
      setReverseSort(!reverseSort)
    } else {
      setSortedBy(sortBy)
      setReverseSort(false)
    }
  }

  const handleInviteClick = async (e) => {
    e.preventDefault()
    setInviteError("")

    if (!userId) {
      setInviteError("Please enter a membership number")
      return
    }

    if (members.some(member => member.id === userId)) {
      setInviteError("User is already a member of the club")
      return
    }

    try {
      await sendInvite({ userId, clubId: club.clubId }).unwrap()

      setUserId("")
      toast.success("Invite sent")

    } catch (error) {
      console.log(error)
      if (error?.status === "functions/not-found") {
        setInviteError("Could not find user. Please enter a valid membership number beginning with GBM")
      } else if (error?.status === "functions/failed-precondition") {
        setInviteError(error?.data?.message)
      } else {
        setInviteError("Error sending invite")
      }
    }
  }

  const handleDeleteClicked = async (data) => {
    try {
      await removeUserFromClub(data).unwrap()
      toast.success("User removed from club")
    } catch (error) {
      console.log(error)
      if (error?.data?.message === "User is a staff member") {
        toast.error("Staff members must be unassigned from their position before being removed.")
      } else {
        toast.error("Error removing user")
      }
    }
  }

  const handleViewUserClicked = (id) => {
    const path = `/admin/members/view-user/${id}`
    navigate(path)
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "Error retrieving club members")
    return <DisplayErrorMessage>Error retrieving club members</DisplayErrorMessage>
  }
  if (!isSuccess || !membersData) return null

  // Transform data and create rows for the table
  const members = transformClubMembers(membersData, club.staffById)
  const searchData = filterBySearch(searchBy, members)
  const sortedData = filterBySortBy(sortedBy, searchData, reverseSort)

  let tableContent

  if (sortedData.length === 0) {
    tableContent = (
      <tr>
        <td colSpan={4} className="text-center">
          No members found
        </td>
      </tr>
    )
  } else {
    tableContent = sortedData.map((member) => {
      return (
        <tr key={member.id}>
          <td className="">{member.name}</td>
          <td className="">{member.id}</td>
          <td className="col-3">{member.email}</td>
          <td className="">
            <div className="d-flex flex-column gap-1 align-items-center">
              <PositionBadges positions={member.positions} />
            </div>
          </td>
          {role === "admin" && (
            <td className="col-auto">
              {/* ACTION BUTTONS */}
              <div className="d-flex justify-content-center gap-2 p-0 m-0">
                {/* View user button */}
                <ViewUserButton action={() => handleViewUserClicked(member.id)} />

                {/* Add position button */}
                <AddPosition clubId={club.clubId} currentPositions={member.positions} currentEmail={member.email} userId={member.id}>
                  <TrBtn text="Add position">
                    <CirclePlus size={15} />
                  </TrBtn>
                </AddPosition>

                {/* Remove member button */}
                <TypeToConfirmButton
                  action={() => handleDeleteClicked({ clubId: club.clubId, userId: member.id })}
                  isLoading={deleting}
                  title="Remove member from club?"
                  message="This action cannot be undone"
                  buttonText="Confirm"
                  confirmMessage="Please type users membership number to confirm."
                  confirmString={member.id}
                >
                  <TrBtn text="Remove from club">
                    <X size={15} />
                  </TrBtn>
                </TypeToConfirmButton>
              </div>
            </td>
          )}
        </tr>
      )
    })
  }

  return (
    <div className="m-0 p-0 w-100">
      <Col className="d-flex flex-column justify-content-center align-items-center">


        <div className="w-100 rounded-4 p-4 shadow-sm bg-white">

          <h4 className="mb-4 mt-3 fw-bold ps-2">Club Members</h4>
          <div className="d-flex flex-wrap w-100">
            {/* BUTTONS */}

            {/* Search bar */}
            <Form style={{ width: "200px" }} className="mb-2 me-2">
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
              />
            </Form>

            {/* Sort by dropdown */}
            <Dropdown className="me-2 mb-2">
              <Dropdown.Toggle variant="" className="text-muted border" id="dropdown-basic">
                Sort by
              </Dropdown.Toggle>
              <Dropdown.Menu className="border-0 shadow fs-7">
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("name")}>Name</Dropdown.Item>
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("num")}>GBM Num</Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => handleSortBy("position")}>Position</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Invite button */}
            <Form onSubmit={handleInviteClick} className="mb-2">
              <InputGroup>
                <Form.Control
                  isInvalid={inviteError}
                  type="text"
                  placeholder="Invite by GBM num"
                  value={userId}
                  onChange={onUserIdChanged}
                />
                <Button type="submit" variant="dark" className="rounded-end-3" disabled={inviteSending}>
                  <LoadingTextSwap isLoading={inviteSending}>
                    Invite
                  </LoadingTextSwap>
                </Button>
              </InputGroup>
              {inviteError && (
                <div className="invalid-feedback d-block fw-bold">
                  {inviteError}
                </div>
              )}
            </Form>

          </div>
          <Table hover responsive style={{ minWidth: "800px" }}>
            <thead>
              <tr>
                <th className="">Name</th>
                <th className="">GBM Num</th>
                <th className="col-3">Email</th>
                <th className=" text-center">Positions</th>
                {role === "admin" && <th className="col-auto text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {tableContent}
            </tbody>
          </Table>
        </div>
      </Col>
    </div>
  )
}

export default ViewClubMembers